/**
 * Staff Accounts — the modern equivalent of the legacy `accounts.php`
 * (CRM `customer_advocate` management). Lists staff accounts and lets an admin
 * add/edit them: profile fields, role assignment, the 8 capability flags, and
 * the `frozen` soft-delete. Selecting a role cascades its capability flags.
 */
import { useState } from 'react';
import type { ColDef } from 'ag-grid-community';
import {
  listAdvocates,
  createAdvocate,
  updateAdvocate,
  listRoles,
} from '../api/advocates-api';
import type { Advocate, Role } from '../api/types';
import { useAsync } from '../hooks/useAsync';
import { LoadingBlock, ErrorBanner } from '../components/ui';
import { DataGrid } from '../components/DataGrid';
import { Modal } from '../neon/Modal';
import { Button, TextInput, SelectInput, CheckboxInput, StatusTag } from '../neon/controls';
import { useDomId } from '../hooks/useDomId';

type FlagKey =
  | 'is_sales_rep'
  | 'is_support_rep'
  | 'can_admin_crm_accounts'
  | 'can_admin_accounts'
  | 'can_access_succeed'
  | 'is_restricted_super_user'
  | 'can_edit_sso_succeed'
  | 'can_edit_powerschool_settings';

const FLAGS: { key: FlagKey; label: string }[] = [
  { key: 'is_sales_rep', label: 'Sales rep' },
  { key: 'is_support_rep', label: 'Support rep' },
  { key: 'can_admin_crm_accounts', label: 'Admin CRM staff accounts' },
  { key: 'can_admin_accounts', label: 'Admin tenant accounts' },
  { key: 'can_access_succeed', label: 'Access Succeed (superuser)' },
  { key: 'is_restricted_super_user', label: 'Restricted super user' },
  { key: 'can_edit_sso_succeed', label: 'Edit SSO settings in Succeed' },
  { key: 'can_edit_powerschool_settings', label: 'Edit PowerSchool settings' },
];

/** Maps each account flag column to the matching camelCase field on a Role. */
const ROLE_FLAG: Record<FlagKey, keyof Role> = {
  is_sales_rep: 'isSalesRep',
  is_support_rep: 'isSupportRep',
  can_admin_crm_accounts: 'canAdminCrmAccounts',
  can_admin_accounts: 'canAdminAccounts',
  can_access_succeed: 'canAccessSucceed',
  is_restricted_super_user: 'isRestrictedSuperUser',
  can_edit_sso_succeed: 'canEditSsoSucceed',
  can_edit_powerschool_settings: 'canEditPowerschoolSettings',
};

export function StaffAccountsPage() {
  const [includeFrozen, setIncludeFrozen] = useState(false);
  const [filter, setFilter] = useState('');
  const [editing, setEditing] = useState<Advocate | 'new' | null>(null);
  const advocates = useAsync(() => listAdvocates(includeFrozen), [includeFrozen]);
  const roles = useAsync(() => listRoles(), []);

  const rows = advocates.data?.advocates ?? [];
  const roleList = roles.data?.roles ?? [];
  const roleTitle = (id: number | null) => roleList.find((r) => r.id === id)?.title ?? '—';

  const columns: ColDef<Advocate>[] = [
    {
      headerName: 'Name',
      valueGetter: (p) => [p.data?.firstName, p.data?.lastName].filter(Boolean).join(' ') || '—',
      minWidth: 160,
    },
    { headerName: 'Username', field: 'username', minWidth: 130 },
    { headerName: 'Email', field: 'email', minWidth: 200 },
    { headerName: 'Abbrev', field: 'abbrev', maxWidth: 110 },
    { headerName: 'Ext', field: 'phone', maxWidth: 100 },
    { headerName: 'Role', valueGetter: (p) => roleTitle(p.data?.roleId ?? null), maxWidth: 150 },
    { headerName: 'Sales', valueGetter: (p) => (p.data?.isSalesRep ? 'Yes' : 'No'), maxWidth: 90 },
    { headerName: 'Support', valueGetter: (p) => (p.data?.isSupportRep ? 'Yes' : 'No'), maxWidth: 100 },
    { headerName: 'CRM admin', valueGetter: (p) => (p.data?.canAdminCrmAccounts ? 'Yes' : 'No'), maxWidth: 120 },
    { headerName: 'Status', valueGetter: (p) => (p.data?.frozen ? 'Frozen' : 'Active'), maxWidth: 110 },
  ];

  if (advocates.loading && !advocates.data) return <LoadingBlock label="Loading staff accounts…" />;
  if (advocates.error) return <ErrorBanner error={advocates.error} />;

  return (
    <div className="page">
      <div className="page-header">
        <h2>Staff Accounts</h2>
        <div className="header-actions">
          <label className="inline-check">
            <input
              type="checkbox"
              checked={includeFrozen}
              onChange={(e) => setIncludeFrozen(e.target.checked)}
            />
            Show frozen
          </label>
          <Button
            id="staff-new"
            text="New staff account"
            icon="add"
            onClick={() => setEditing('new')}
            ariaHaspopup="dialog"
          />
        </div>
      </div>

      <div className="search-bar">
        <input
          type="text"
          placeholder="Filter by name, username, email…"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          style={{ maxWidth: 360 }}
        />
      </div>

      <div className="meta">{rows.length} staff account{rows.length === 1 ? '' : 's'}</div>

      <DataGrid
        rows={rows}
        columns={columns}
        quickFilter={filter}
        loading={advocates.loading}
        getRowId={(r) => String(r.id)}
        onRowClick={(r) => setEditing(r)}
      />

      {editing && (
        <StaffAccountModal
          advocate={editing === 'new' ? null : editing}
          roles={roleList}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            advocates.reload();
          }}
        />
      )}
    </div>
  );
}

interface StaffForm {
  username: string;
  first_name: string;
  last_name: string;
  email: string;
  fk_contact_id: string;
  phone: string;
  abbrev: string;
  signature: string;
  customer_advocate_role_id: string;
  is_sales_rep: boolean;
  is_support_rep: boolean;
  can_admin_crm_accounts: boolean;
  can_admin_accounts: boolean;
  can_access_succeed: boolean;
  is_restricted_super_user: boolean;
  can_edit_sso_succeed: boolean;
  can_edit_powerschool_settings: boolean;
  frozen: boolean;
}

function initForm(a: Advocate | null): StaffForm {
  return {
    username: a?.username ?? '',
    first_name: a?.firstName ?? '',
    last_name: a?.lastName ?? '',
    email: a?.email ?? '',
    fk_contact_id: a?.fkContactId != null ? String(a.fkContactId) : '',
    phone: a?.phone ?? '',
    abbrev: a?.abbrev ?? '',
    signature: a?.signature ?? '',
    customer_advocate_role_id: a?.roleId != null ? String(a.roleId) : '',
    is_sales_rep: a?.isSalesRep ?? false,
    is_support_rep: a?.isSupportRep ?? false,
    can_admin_crm_accounts: a?.canAdminCrmAccounts ?? false,
    can_admin_accounts: a?.canAdminAccounts ?? false,
    can_access_succeed: a?.canAccessSucceed ?? false,
    is_restricted_super_user: a?.isRestrictedSuperUser ?? false,
    can_edit_sso_succeed: a?.canEditSsoSucceed ?? false,
    can_edit_powerschool_settings: a?.canEditPowerschoolSettings ?? false,
    frozen: a?.frozen ?? false,
  };
}

function StaffAccountModal({
  advocate,
  roles,
  onClose,
  onSaved,
}: {
  advocate: Advocate | null;
  roles: Role[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const isNew = !advocate;
  const baseId = useDomId('staff');
  const [form, setForm] = useState<StaffForm>(() => initForm(advocate));
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const roleSelected = form.customer_advocate_role_id !== '';
  const roleOptions = roles.map((r) => ({ value: r.id, label: r.title }));

  const setField = <K extends keyof StaffForm>(k: K, v: StaffForm[K]) =>
    setForm((f) => ({ ...f, [k]: v }));

  const onRoleChange = (val: string) => {
    const role = roles.find((r) => String(r.id) === val);
    setForm((f) => {
      const next: StaffForm = { ...f, customer_advocate_role_id: val };
      if (role) for (const { key } of FLAGS) next[key] = Boolean(role[ROLE_FLAG[key]]);
      return next;
    });
  };

  const submit = async () => {
    setError('');
    if (!form.username.trim()) {
      setError('Username is required.');
      return;
    }
    const payload: Record<string, unknown> = {
      username: form.username.trim(),
      first_name: form.first_name,
      last_name: form.last_name,
      email: form.email,
      fk_contact_id: form.fk_contact_id === '' ? null : Number(form.fk_contact_id),
      phone: form.phone,
      abbrev: form.abbrev,
      signature: form.signature,
      customer_advocate_role_id: form.customer_advocate_role_id === '' ? '' : Number(form.customer_advocate_role_id),
      frozen: form.frozen ? 1 : 0,
    };
    for (const { key } of FLAGS) payload[key] = form[key] ? 1 : 0;
    setBusy(true);
    try {
      if (isNew) await createAdvocate(payload);
      else await updateAdvocate(advocate!.id, payload);
      onSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save the staff account.');
      setBusy(false);
    }
  };

  const footer = (
    <>
      <Button
        id={`${baseId}-save`}
        text={isNew ? 'Create account' : 'Save account'}
        variant="primary"
        onClick={submit}
        loading={busy}
      />
      <Button id={`${baseId}-cancel`} text="Cancel" variant="tertiary" onClick={onClose} />
    </>
  );

  return (
    <Modal
      title={isNew ? 'New staff account' : 'Edit staff account'}
      size="large"
      onClose={onClose}
      footer={footer}
    >
      <div className="modal-form">
        {error ? <p className="form-error">{error}</p> : null}
        {advocate?.frozen ? (
          <p className="subtle">
            <StatusTag status="Past" /> This account is frozen (sign-in disabled).
          </p>
        ) : null}

        <div className="form-grid">
          <TextInput id={`${baseId}-username`} label="Username" value={form.username} onChange={(v) => setField('username', v)} required />
          <TextInput id={`${baseId}-first`} label="First name" value={form.first_name} onChange={(v) => setField('first_name', v)} />
          <TextInput id={`${baseId}-last`} label="Last name" value={form.last_name} onChange={(v) => setField('last_name', v)} />
          <TextInput id={`${baseId}-email`} label="Email" value={form.email} onChange={(v) => setField('email', v)} email />
          <TextInput id={`${baseId}-contact`} label="WK12 contact ID" value={form.fk_contact_id} onChange={(v) => setField('fk_contact_id', v)} />
          <TextInput id={`${baseId}-phone`} label="Phone extension" value={form.phone} onChange={(v) => setField('phone', v)} />
          <TextInput id={`${baseId}-abbrev`} label="Abbreviation" value={form.abbrev} onChange={(v) => setField('abbrev', v)} />
        </div>

        <div className="field">
          <label className="field-label" htmlFor={`${baseId}-sig`}>Signature</label>
          <textarea
            id={`${baseId}-sig`}
            rows={4}
            value={form.signature}
            onChange={(e) => setField('signature', e.target.value)}
          />
        </div>

        <SelectInput
          id={`${baseId}-role`}
          label="Role"
          value={form.customer_advocate_role_id}
          onChange={onRoleChange}
          options={roleOptions}
          allowEmpty
          emptyText="N/A — set capabilities manually"
        />

        <fieldset className="flag-grid">
          <legend>Capabilities{roleSelected ? ' — governed by the selected role' : ''}</legend>
          {FLAGS.map((f) => (
            <CheckboxInput
              key={f.key}
              id={`${baseId}-${f.key}`}
              label={f.label}
              checked={form[f.key]}
              disabled={roleSelected}
              onChange={(v) => setField(f.key, v)}
              toggle
            />
          ))}
        </fieldset>

        {!isNew ? (
          <CheckboxInput
            id={`${baseId}-frozen`}
            label="Frozen — soft-delete (disables sign-in)"
            checked={form.frozen}
            onChange={(v) => setField('frozen', v)}
            toggle
          />
        ) : null}
      </div>
    </Modal>
  );
}
